import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CategoryButtons from './components/CategoryButtons';
import FAQSection from './components/FAQSection';
import PillarArticles from './components/PillarArticles';
import ProductShowcase from './components/ProductShowcase';
import { getSiteUrl, FAVICON_URL } from '@/lib/seo';

const resources = [
  { key: 'spreadsheet', icon: 'ri-table-line' },
  { key: 'review', icon: 'ri-star-line' },
  { key: 'shipping', icon: 'ri-truck-line' },
  { key: 'warehouse', icon: 'ri-building-2-line' },
  { key: 'qcGuide', icon: 'ri-check-double-line' },
  { key: 'coupon', icon: 'ri-coupon-line' },
  { key: 'discount', icon: 'ri-percent-line' },
  { key: 'legit', icon: 'ri-shield-check-line' },
];

const benefits = [
  { key: 'find', icon: 'ri-search-line' },
  { key: 'compare', icon: 'ri-arrow-left-right-line' },
  { key: 'qc', icon: 'ri-check-double-line' },
  { key: 'research', icon: 'ri-article-line' },
];

export default function Home() {
  const { t } = useTranslation();

  const siteUrl = getSiteUrl();
  const schemaOrganization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'CSSBuy Spreadsheet',
    url: siteUrl,
    logo: FAVICON_URL,
  };
  const schemaWebSite = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'CSSBuy Spreadsheet',
    url: siteUrl,
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(schemaOrganization)}</script>
      <script type="application/ld+json">{JSON.stringify(schemaWebSite)}</script>

      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Overhead%20flatlay%20of%20assorted%20premium%20fashion%20products%20including%20colorful%20sneakers%20a%20denim%20jacket%20folded%20tshirts%20and%20a%20stylish%20cap%20arranged%20on%20a%20soft%20warm%20cream%20background%20with%20natural%20window%20light%20editorial%20product%20photography%20high%20detail%20modern%20lifestyle%20ecommerce%20aesthetic&width=1600&height=900&seq=hero-fashion-01&orientation=landscape&nocache=true"
            alt="CSSBuy Spreadsheet products"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        </div>
        <div className="relative z-10 px-4 md:px-6 pt-20 w-full">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block text-xs md:text-sm font-semibold tracking-widest text-white/80 uppercase mb-4">
              {t('hero.eyebrow')}
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {t('hero.title')}
            </h1>
            <p className="text-base md:text-lg text-white/85 max-w-2xl mx-auto mb-8 leading-relaxed">
              {t('hero.description')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 bg-primary-500 text-white font-medium px-8 py-3 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('hero.primaryCta')}
                <i className="ri-arrow-right-line" />
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 bg-white/15 text-white font-medium px-8 py-3 rounded-lg hover:bg-white/25 backdrop-blur-sm transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('hero.secondaryCta')}
              </a>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
              <span className="flex items-center gap-2 text-sm text-white/80">
                <i className="ri-check-line text-primary-300" />
                {t('hero.trust1')}
              </span>
              <span className="flex items-center gap-2 text-sm text-white/80">
                <i className="ri-check-line text-primary-300" />
                {t('hero.trust2')}
              </span>
              <span className="flex items-center gap-2 text-sm text-white/80">
                <i className="ri-check-line text-primary-300" />
                {t('hero.trust3')}
              </span>
              <span className="flex items-center gap-2 text-sm text-white/80">
                <i className="ri-check-line text-primary-300" />
                {t('hero.trust4')}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* What Is */}
      <section className="py-16 md:py-20 bg-white">
        <div className="px-4 md:px-6">
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-10 md:gap-16">
            <div className="w-full md:w-1/2">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20minimal%20spreadsheet%20and%20data%20dashboard%20displayed%20on%20a%20clean%20laptop%20and%20tablet%20setup%20with%20soft%20product%20photos%20and%20organized%20tables%20on%20a%20bright%20airy%20workspace%20background%20editorial%20tech%20photography%20clean%20aesthetic&width=800&height=600&seq=whatis-spreadsheet-01&orientation=landscape&nocache=true"
                alt="CSSBuy Spreadsheet organization"
                className="w-full rounded-xl object-cover"
              />
            </div>
            <div className="w-full md:w-1/2">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                {t('whatIs.title')}
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-6 text-sm md:text-base">
                {t('whatIs.description')}
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 text-primary-600 font-medium hover:text-primary-700 cursor-pointer"
              >
                {t('whatIs.cta')}
                <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                {t('benefits.title')}
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((b) => (
                <div key={b.key} className="bg-white rounded-lg p-6 border border-background-200/70 hover:border-primary-200 transition-colors">
                  <div className="w-12 h-12 rounded-lg bg-primary-50 flex items-center justify-center mb-4">
                    <i className={`${b.icon} text-primary-600 text-xl`} />
                  </div>
                  <h3 className="font-semibold text-foreground-900 mb-2">
                    {t(`benefits.${b.key}`)}
                  </h3>
                  <p className="text-sm text-foreground-600">
                    {t(`benefits.${b.key}Desc`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Category Buttons */}
      <CategoryButtons />

      {/* Product Showcase - NEW */}
      <ProductShowcase />

      {/* Popular Resources */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                {t('popularResources.title')}
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {resources.map((r) => (
                <a
                  key={r.key}
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="group flex items-center gap-4 p-4 bg-white rounded-lg border border-background-200/70 hover:border-primary-200 hover:bg-primary-50 transition-all cursor-pointer"
                >
                  <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center group-hover:bg-primary-100 transition-colors">
                    <i className={`${r.icon} text-secondary-600 group-hover:text-primary-600 text-lg`} />
                  </div>
                  <span className="font-medium text-foreground-800 text-sm group-hover:text-primary-700">
                    {t(`popularResources.${r.key}`)}
                  </span>
                  <i className="ri-arrow-right-line ml-auto text-foreground-400 group-hover:text-primary-500 text-sm" />
                </a>
              ))}
            </div>

            {/* Article Link */}
            <div className="mt-8 bg-white rounded-xl border border-primary-200/50 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-book-open-line text-primary-600 text-xl" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground-900 mb-1">
                  CSSBuy Spreadsheet: Complete Guide to Finding & Comparing Products
                </h3>
                <p className="text-sm text-foreground-600">
                  Learn how to use CSSBuy Spreadsheet for product research, QC verification, shipping comparisons, and smart buying decisions. Independent guide updated for 2026.
                </p>
              </div>
              <Link
                to="/pt/cssbuy-spreadsheet-guide"
                className="inline-flex items-center gap-2 bg-primary-500 text-white font-medium px-5 py-2.5 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap text-sm cursor-pointer flex-shrink-0"
              >
                {t('article.readMore')}
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Beginner + Shipping + Safety + Comparison */}
      <section className="py-16 md:py-20 bg-white">
        <div className="px-4 md:px-6">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-background-50 rounded-xl p-8 border border-background-200/70">
              <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center mb-4">
                <i className="ri-rocket-line text-primary-600 text-xl" />
              </div>
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">
                {t('beginner.title')}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                {t('beginner.description')}
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 text-primary-600 font-medium hover:text-primary-700 text-sm cursor-pointer"
              >
                {t('beginner.cta')}
                <i className="ri-arrow-right-line" />
              </a>
            </div>

            <div className="bg-background-50 rounded-xl p-8 border border-background-200/70">
              <div className="w-12 h-12 rounded-lg bg-accent-100 flex items-center justify-center mb-4">
                <i className="ri-truck-line text-accent-600 text-xl" />
              </div>
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">
                {t('shipping.title')}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                {t('shipping.description')}
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 text-accent-600 font-medium hover:text-accent-700 text-sm cursor-pointer"
              >
                {t('shipping.cta')}
                <i className="ri-arrow-right-line" />
              </a>
            </div>

            <div className="bg-background-50 rounded-xl p-8 border border-background-200/70">
              <div className="w-12 h-12 rounded-lg bg-secondary-100 flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-secondary-600 text-xl" />
              </div>
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">
                {t('safety.title')}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                {t('safety.description')}
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 text-secondary-600 font-medium hover:text-secondary-700 text-sm cursor-pointer"
              >
                {t('safety.cta')}
                <i className="ri-arrow-right-line" />
              </a>
            </div>

            <div className="bg-background-50 rounded-xl p-8 border border-background-200/70">
              <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center mb-4">
                <i className="ri-bar-chart-box-line text-primary-600 text-xl" />
              </div>
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">
                {t('comparison.title')}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                {t('comparison.description')}
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 text-primary-600 font-medium hover:text-primary-700 text-sm cursor-pointer"
              >
                {t('comparison.cta')}
                <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pillar Articles */}
      <PillarArticles />

      {/* FAQ */}
      <FAQSection />

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-primary-500 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20abstract%20gradient%20pattern%20in%20warm%20coral%20and%20cream%20tones%20with%20subtle%20organic%20shapes%20and%20gentle%20light%20texture%20minimal%20modern%20background%20design%20no%20text%20clean%20aesthetic&width=1600&height=600&seq=final-cta-bg-01&orientation=landscape&nocache=true"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
              {t('finalCta.title')}
            </h2>
            <p className="text-white/85 mb-8 leading-relaxed text-sm md:text-base">
              {t('finalCta.description')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 bg-white text-primary-600 font-medium px-8 py-3 rounded-lg hover:bg-white/90 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('finalCta.primary')}
                <i className="ri-arrow-right-line" />
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 bg-white/15 text-white font-medium px-8 py-3 rounded-lg hover:bg-white/25 backdrop-blur-sm transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('finalCta.secondary')}
                <i className="ri-shopping-cart-line" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer bar */}
      <div className="bg-background-100 border-t border-background-200/70 py-4">
        <div className="px-4 md:px-6 max-w-6xl mx-auto text-center">
          <p className="text-xs text-foreground-500">
            {t('disclaimer')}
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}